import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { getPaymentStatus } from "../api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderId, setOrderId] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-24">
      <div className="max-w-lg w-full text-center bg-[#141210] border border-[#2c2822] rounded-2xl px-8 py-14">
        {state === "checking" && (
          <>
            <Loader2 size={54} className="text-[#c9a24b] mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl text-[#f4efe4] mb-3">Confirming your payment</h1>
            <p className="text-[#a29a8c] text-sm">Just a moment while we verify your order...</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={60} className="text-[#c9a24b] mx-auto mb-6" strokeWidth={1.4} />
            <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-4">Order Confirmed</p>
            <h1 className="font-display text-4xl text-[#f4efe4] mb-4">Thank you</h1>
            <p className="text-[#a29a8c] mb-2">Your payment was successful and your ritual is on its way.</p>
            {orderId && (
              <p className="text-[#6b6459] text-xs mb-8">Order ref: {orderId.slice(0, 8).toUpperCase()}</p>
            )}
            <Link to="/shop" className="btn-gold inline-flex px-9 py-3 rounded-full text-sm tracking-[0.15em] uppercase">
              Continue Shopping
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={54} className="text-[#c9a24b] mx-auto mb-6" />
            <h1 className="font-display text-3xl text-[#f4efe4] mb-3">Payment processing</h1>
            <p className="text-[#a29a8c] text-sm mb-8">
              Your payment is being processed. You'll receive a confirmation by email shortly.
            </p>
            <Link to="/" className="btn-outline-gold inline-flex px-9 py-3 rounded-full text-sm tracking-[0.15em] uppercase">
              Back to Home
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle size={54} className="text-[#c9a24b] mx-auto mb-6" strokeWidth={1.4} />
            <h1 className="font-display text-3xl text-[#f4efe4] mb-3">We couldn't confirm this</h1>
            <p className="text-[#a29a8c] text-sm mb-8">
              If you were charged, your order is safe and we'll email your confirmation. Otherwise please try again.
            </p>
            <Link to="/shop" className="btn-outline-gold inline-flex px-9 py-3 rounded-full text-sm tracking-[0.15em] uppercase">
              Return to Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
